import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import Inputfile from 'shared/components/inputfile';
import { LabelRequiredInput, LabelError, LabelInput } from 'shared/components/Labels';
import DropdownSearch from 'shared/components/DropdownSearch';
import { TextField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import MainSpinner from 'shared/components/MainSpinner';
import AlertModal from 'shared/components/AlertModal';

import { createNewContact, setAddContacts } from 'redux/actions/contactActions';
import useApi from 'hooks/useApi';
import { setExecuted, showAlert } from 'redux/actions/alertActions';
import useLanguages from 'hooks/useLanguages';
import { removeBreadcrumb } from 'redux/actions/layoutActions';

import { ClientProps } from 'shared/prop-types/ReducerProps';
import {
  ALERT_TYPE_ERROR,
  ALERT_TYPE_SUCCESS,
  cleanSpaceString,
  toBase64,
  typeOrganizations,
} from 'shared/utils';
import { colors } from 'shared/defaultTheme';
import CustomSelect from 'shared/components/CustomSelect';

import MultipleSelectSearch from 'shared/components/MultipleSelectSearch';
import Contacts from './Contacts';
import { DocumentDropdownSearch } from '../../../shared/components/DocumentForm/styles';

import { BasicDataWrapper } from '../styles';

const BasicDataForm = ({
  updating,
  currentClient,
  setNewBasicData,
  setShowMessage,
  setIsSaveBasicData,
  isSaveBasicData,
  i18Tags,
}) => {
  const {
    control,
    handleSubmit: handleSubmitBasicData,
    formState: { errors },
    setValue,
    reset,
    watch,
    getValues,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      ...currentClient,
    },
  });
  const history = useHistory();
  const { customAreaApi } = useApi();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const { languages } = useLanguages();
  const { alertType, executeActionCloseAlert } = useSelector((state) => state.alertState);
  const { addContacts } = useSelector((state) => state.contact);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const newTypeOrganization = useRef(null);
  const newClientName = useRef(null);
  const newContactPhone = useRef(null);
  const newClientI18 = useRef(null);
  const newCif = useRef(null);
  const newDirection = useRef(null);
  const newCp = useRef(null);
  const newState = useRef(null);
  const newLocal = useRef(null);
  const newStateClient = useRef(null);
  const newTagsClient = useRef(null);
  const formRef = useRef();
  const [logo, setLogo] = useState(null);
  const [errorFile, setErrorFile] = useState('');
  const [clientId, setClientId] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [languageField, setLanguageField] = useState('');
  const [states, setStates] = useState([]);
  const [currentI18Tags, setCurrentI18Tags] = useState([]);
  newTypeOrganization.current = watch('typeOrganization', '');
  newClientName.current = watch('clientName', '');
  newContactPhone.current = watch('contactPhone', '');
  newClientI18.current = watch('clientI18', '');
  newCif.current = watch('cif', '');
  newDirection.current = watch('direction', '');
  newCp.current = watch('cp', '');
  newState.current = watch('state', '');
  newLocal.current = watch('local', '');
  newStateClient.current = watch('stateClient', '');
  newTagsClient.current = watch('tagsClient', '');
  const imageValidTypes = ['jpeg', 'jpg', 'png', 'gif', 'svg', 'image'];

  const [{ loading }, post] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  useEffect(() => {
    if (!auth.isCustomer) {
      history.push('/home');
    }
    return () => {
      dispatch(setAddContacts([]));
    };
  }, []);

  const [, getFilterClientByCif] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${auth.cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, createClientContacts] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${auth.cusNavId}`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getStatesData] = customAreaApi(
    {
      url: `${window.API_V1}/cus/StatesClients/${auth.cusNavId}`,
      method: 'GET',
    },
    { manual: true }
  );

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  useEffect(() => {
    const getClientStates = async () => {
      try {
        const searchParam = {
          filters: [
            {
              model: 'StateClient',
              field: 'Name',
              asc: true,
            },
          ],
          reduce: {
            limit: 0,
            skip: 0,
          },
        };
        const {
          data: { data },
        } = await getStatesData({ data: searchParam });
        setStates(data);
      } catch (error) {
        console.error('getClientStates ---> error:', error);
        setStates([]);
      }
    };

    const fetchData = async () => {
      await getClientStates();
    };

    fetchData();
  }, []);

  useEffect(() => {
    let newI18Tags = [];
    i18Tags.forEach((tag) => {
      newI18Tags = [...newI18Tags, { name: getNameSelectedLanguage(tag.i18List), id: tag.id }];
    });
    setCurrentI18Tags(newI18Tags);
  }, [selectedDefaultLanguage]);

  useEffect(() => {
    if (currentClient) {
      setLogo(currentClient.logo);
      setValue('clientI18', currentClient.clientI18);
      setValue('clientName', currentClient.clientName);
      setValue('cif', currentClient.cif);
      setValue('typeOrganization', currentClient.typeOrganization);
      setValue('contactPhone', currentClient.contactPhone);
      setValue('direction', currentClient.direction);
      setValue('cp', currentClient.cp);
      setValue('state', currentClient.state);
      setValue('local', currentClient.local);
      setValue('stateClient', currentClient.stateClient);
      setValue('tagsClient', currentClient.tagsClient);
    }
  }, [currentClient]);

  useEffect(() => {
    if (currentClient) {
      const values = getValues();
      const newData = { logo, ...values };
      setNewBasicData(newData);
      setShowMessage(false);
    }
  }, [
    newContactPhone.current,
    newClientName.current,
    newTypeOrganization.current,
    newClientI18.current,
    newCif.current,
    newDirection.current,
    newCp.current,
    newState.current,
    newLocal.current,
    newStateClient.current,
    newTagsClient.current,
    logo,
    currentClient,
  ]);

  useEffect(() => {
    if (executeActionCloseAlert && alertType !== ALERT_TYPE_ERROR && !updating && clientId) {
      dispatch(setExecuted());
      history.push(`/clients/update/${clientId}`);
    }
  }, [executeActionCloseAlert, clientId]);

  const onToggleWarning = () => {
    setShowWarning((currentShow) => !currentShow);
    dispatch(createNewContact());
  };

  const validationCif = async (cif) => {
    try {
      await getFilterClientByCif({
        data: {
          filters: [
            {
              model: 'Client',
              field: 'Cif',
              filter: [cif],
            },
          ],
          reduce: {
            limit: 0,
            skip: 0,
          },
        },
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  const onSubmitBasicData = async (values, e) => {
    if (e.target.id === 'basic-data-form') {
      if (!updating && addContacts.length === 0) {
        setShowWarning(true);
      } else {
        try {
          const {
            clientI18,
            clientName,
            cif,
            typeOrganization,
            contactPhone,
            direction,
            cp,
            state,
            local,
            stateClient,
            tagsClient,
          } = values;
          const url = updating
            ? `${window.API_V1}/cli/clients/${auth.cusNavId}/modify`
            : `${window.API_V1}/cli/clients/${auth.cusNavId}`;
          const data = updating
            ? {
                clientId: currentClient.clientId,
                clientI18,
                clientName: clientName.trimEnd(),
                cif: cif.trimEnd(),
                logo,
                typeOrganization,
                contactPhone,
                direction,
                cp,
                state,
                local,
                stateClient,
                tagsClient,
              }
            : {
                clientI18,
                clientName,
                cif,
                logo,
                typeOrganization,
                contactPhone,
                direction,
                cp,
                state,
                local,
                stateClient,
                tagsClient,
              };

          if (currentClient && cif.trimEnd() !== currentClient.cif) {
            const existCif = await validationCif(cif.trimEnd());
            if (existCif) {
              dispatch(
                showAlert({
                  alertType: ALERT_TYPE_ERROR,
                  alertMessage: t('client_management_duplicate_nif'),
                })
              );
              return;
            }
          }
          const {
            data: { errors: errorsFromPost, infos, data: basicData },
          } = await post({
            url,
            data,
          });
          if (errorsFromPost.length) {
            dispatch(
              showAlert({
                alertType: ALERT_TYPE_ERROR,
                alertMessage: t('client_management_error_msg'),
              })
            );
            return;
          }
          if (basicData === null) {
            const existDuplicateNif = infos.some((x) => x.code === 'CC-02');
            if (existDuplicateNif) {
              dispatch(
                showAlert({
                  alertType: ALERT_TYPE_ERROR,
                  alertMessage: t('client_management_duplicate_nif'),
                })
              );
            }
            return;
          }
          if (!updating) {
            const newContacts = addContacts.map((contact) => {
              return {
                ...contact,
                clients: [
                  {
                    clientId: basicData.clientId,
                  },
                ],
              };
            });

            await createClientContacts({
              data: newContacts,
            });

            dispatch(removeBreadcrumb());
            setClientId(basicData.clientId);
            dispatch(
              showAlert({
                alertType: ALERT_TYPE_SUCCESS,
                alertMessage: t('client_management_created_success_msg'),
              })
            );
            return;
          }
          dispatch(
            showAlert({
              alertType: ALERT_TYPE_SUCCESS,
              alertMessage: t('client_management_updated_success_msg'),
            })
          );
          setIsSaveBasicData(false);
        } catch (ex) {
          if (ex?.response?.data.errors[0].code === 'CCMC-01') {
            dispatch(
              showAlert({
                alertType: ALERT_TYPE_ERROR,
                alertMessage: t('client_management_error_nif'),
              })
            );
          } else if (ex?.response?.data.errors[0].code === 'CC-02') {
            dispatch(
              showAlert({
                alertType: ALERT_TYPE_ERROR,
                alertMessage: t('client_management_duplicate_nif'),
              })
            );
          } else {
            dispatch(
              showAlert({
                alertType: ALERT_TYPE_ERROR,
                alertMessage: t('client_management_error_msg'),
              })
            );
          }
        }
      }
    }
  };

  useEffect(() => {
    if (isSaveBasicData) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  }, [isSaveBasicData]);

  const onCancel = () => {
    reset();
    setLogo(currentClient ? currentClient.logo : null);
    dispatch(removeBreadcrumb());
    history.push('/clients');
  };

  const onChangeFile = async (file) => {
    const selectedLogo = await toBase64(file);
    setLogo(selectedLogo);
  };

  const onErrorFile = (errorMessage) => {
    setErrorFile(errorMessage);
  };

  return (
    <BasicDataWrapper>
      <Row className="row-basic-data">
        <Col sm={3} className="col-input-file">
          <Inputfile
            className="custom-input-file"
            onChangeFile={onChangeFile}
            validTypes={imageValidTypes}
            showUploadedImage
            uploadedImage={logo}
            placeholder={t('client_placeholder_upload_logo')}
            onError={onErrorFile}
            error={errorFile.length > 0}
          />
          {errorFile && <LabelError label={errorFile} />}
        </Col>
        <Col sm={9} className="col-form">
          <form
            id="basic-data-form"
            className="form"
            onSubmit={handleSubmitBasicData(onSubmitBasicData)}
            noValidate
            ref={formRef}
          >
            <Row className="w-100">
              <Col sm={6}>
                <div className="form__form-group">
                  <LabelRequiredInput
                    className="form__form-group-label"
                    label={t('client_management_field_name')}
                    color={colors.black}
                    error={errors.clientName != null}
                  />
                  <Controller
                    name="clientName"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: { value: true, message: t('general_required_field') },
                      maxLength: {
                        value: 200,
                        message: t('general_max_length_field').replace('{param}', 200),
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={(e) => {
                          const newEvent = cleanSpaceString(e);
                          field.onChange(newEvent);
                        }}
                        onBlur={field.onBlur}
                        error={errors.clientName != null}
                      />
                    )}
                  />
                  {errors.clientName && <LabelError label={errors.clientName.message} />}
                </div>
              </Col>
              <Col sm={6}>
                <div className="form__form-group">
                  <LabelRequiredInput
                    className="form__form-group-label"
                    label={t('client_management_field_id')}
                    color={colors.black}
                  />
                  <Controller
                    name="cif"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: { value: true, message: t('general_required_field') },
                      maxLength: {
                        value: 15,
                        message: t('general_max_length_field').replace('{param}', 15),
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={(e) => {
                          const newEvent = cleanSpaceString(e);
                          field.onChange(newEvent);
                        }}
                        onBlur={field.onBlur}
                        error={errors.cif != null}
                      />
                    )}
                  />
                  {errors.cif && <LabelError label={errors.cif.message} />}
                </div>
              </Col>
            </Row>
            <Row className="w-100">
              <Col sm={6}>
                <div className="form__form-group">
                  <LabelRequiredInput
                    className="form__form-group-label"
                    label={t('client_management_field_type_organization')}
                    color={colors.black}
                  />
                  <Controller
                    name="typeOrganization"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: { value: true, message: t('general_required_field') },
                    }}
                    render={({ field }) => (
                      <DropdownSearch
                        id="type-organization-dropdown-search"
                        options={typeOrganizations.map((item) => ({
                          name: item.name,
                          value: item.id,
                        }))}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        error={errors.typeOrganization !== undefined}
                        search={false}
                        placeholder=" "
                        className="client-dropdown-search"
                      />
                    )}
                  />
                  {errors.typeOrganization && (
                    <LabelError label={errors.typeOrganization.message} />
                  )}
                </div>
              </Col>
              <Col sm={6}>
                <Row>
                  <Col sm={6}>
                    <div className="form__form-group">
                      <LabelInput
                        className="form__form-group-label"
                        label={t('client_management_field_phone')}
                        color={colors.black}
                      />
                      <Controller
                        name="contactPhone"
                        control={control}
                        defaultValue=""
                        rules={{
                          maxLength: {
                            value: 80,
                            message: t('general_max_length_field').replace('{param}', 80),
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.contactPhone != null}
                          />
                        )}
                      />
                      {errors.contactPhone && <LabelError label={errors.contactPhone.message} />}
                    </div>
                  </Col>
                  <Col sm={6}>
                    <LabelInput
                      className="form__form-group-label mr-2"
                      label={t('general_language')}
                      color={colors.black}
                      error={errors.clientName != null}
                    />
                    <Controller
                      name="clientI18"
                      control={control}
                      defaultValue={selectedDefaultLanguage}
                      rules={{ required: { value: true, message: t('general_required_field') } }}
                      render={({ field }) => (
                        <CustomSelect
                          options={languages}
                          defaultValue={field.value}
                          backgroundColor={colors.transparent}
                          onChange={(value) => {
                            field.onChange(value);
                            setLanguageField(value.target.value);
                          }}
                          className="w-100 client-custom-select"
                        />
                      )}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="w-100">
              <Col sm={6}>
                <div className="form__form-group">
                  <LabelInput
                    className="form__form-group-label"
                    label={t('client_management_field_address')}
                    color={colors.black}
                  />
                  <Controller
                    name="direction"
                    control={control}
                    defaultValue=""
                    rules={{
                      maxLength: {
                        value: 150,
                        message: t('general_max_length_field').replace('{param}', 150),
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={errors.direction != null}
                      />
                    )}
                  />
                  {errors.direction && <LabelError label={errors.direction.message} />}
                </div>
              </Col>
              <Col sm={6}>
                <div className="form__form-group">
                  <LabelInput
                    className="form__form-group-label"
                    label={t('client_management_field_cp')}
                    color={colors.black}
                  />
                  <Controller
                    name="cp"
                    control={control}
                    defaultValue=""
                    rules={{
                      maxLength: {
                        value: 5,
                        message: t('general_max_length_field').replace('{param}', 5),
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={errors.cp != null}
                      />
                    )}
                  />
                  {errors.cp && <LabelError label={errors.cp.message} />}
                </div>
              </Col>
            </Row>
            <Row className="w-100">
              <Col sm={6}>
                <div className="form__form-group">
                  <LabelInput
                    className="form__form-group-label"
                    label={t('client_management_field_state')}
                    color={colors.black}
                  />
                  <Controller
                    name="state"
                    control={control}
                    defaultValue=""
                    rules={{
                      maxLength: {
                        value: 80,
                        message: t('general_max_length_field').replace('{param}', 80),
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={errors.state != null}
                      />
                    )}
                  />
                  {errors.state && <LabelError label={errors.state.message} />}
                </div>
              </Col>
              <Col sm={6}>
                <div className="form__form-group">
                  <LabelInput
                    className="form__form-group-label"
                    label={t('client_management_field_city')}
                    color={colors.black}
                  />
                  <Controller
                    name="local"
                    control={control}
                    defaultValue=""
                    rules={{
                      maxLength: {
                        value: 80,
                        message: t('general_max_length_field').replace('{param}', 80),
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={errors.local != null}
                      />
                    )}
                  />
                  {errors.local && <LabelError label={errors.local.message} />}
                </div>
              </Col>
            </Row>
            <Row className="w-100">
              <Col sm={6}>
                <div className="form__form-group">
                  <LabelInput
                    className="form__form-group-label"
                    label={t('Settings_client_status')}
                    color={colors.black}
                  />
                  <Controller
                    name="stateClient"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <DocumentDropdownSearch
                        id="word-document-dropdown-search"
                        options={states.map((item) => ({
                          name: getNameSelectedLanguage(item.i18List),
                          value: item.id,
                          color: item.color,
                        }))}
                        titleNotFound={t('state_not_found')}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        error={errors.state !== undefined}
                        isColor
                        color={
                          field.value ? states.find((item) => item.id === field.value)?.color : ''
                        }
                      />
                    )}
                  />
                  {errors.typeOrganization && (
                    <LabelError label={errors.typeOrganization.message} />
                  )}
                </div>
              </Col>
              <Col sm={6}>
                <LabelInput
                  className="form__form-group-label"
                  label={t('general_text_clasification')}
                  color={colors.black}
                />
                <Controller
                  name="tagsClient"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <MultipleSelectSearch
                      dataSource={currentI18Tags}
                      fields={{ text: 'name', value: 'id' }}
                      defaultValue={field.value.length > 0 ? field.value : undefined}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={errors.tags !== undefined}
                      titleNotFound={t('tag_not_found')}
                    />
                  )}
                />
                {errors.tags && <LabelError label={errors.tags.message} />}
              </Col>
            </Row>
            <span className="text-contact">
              {t('client_management_contact')}
              <span className="ml-1 text-contact-required">
                *{t('client_management_contact_required')}
              </span>
            </span>
            <div className="form-divider" />
            <Row className="w-100">
              <Col sm={12}>
                <Contacts
                  languageField={languageField}
                  setLanguageField={setLanguageField}
                  clientId={currentClient?.clientId ?? ''}
                  updating={updating}
                  newTypeOrganization={newTypeOrganization.current}
                  newClientName={newClientName.current}
                  newContactPhone={newContactPhone.current}
                />
              </Col>
            </Row>
            <Row className="w-100 mt-4">
              <Col sm={12} className="d-flex flex-row justify-content-end">
                <Button
                  className="mr-3 text-uppercase"
                  type="button"
                  color="secondary"
                  label={t('general_button_cancel')}
                  onClick={onCancel}
                />
                <Button
                  className="text-uppercase"
                  type="submit"
                  color="primary"
                  label={updating ? t('general_button_save') : t('general_add')}
                />
              </Col>
            </Row>
          </form>
        </Col>
      </Row>
      {loading && <MainSpinner />}
      <AlertModal
        type="warning"
        isOpen={showWarning}
        toggle={onToggleWarning}
        message={t('client_required_contact')}
        okText={t('general_button_ok')}
        onOk={onToggleWarning}
      />
    </BasicDataWrapper>
  );
};

BasicDataForm.defaultProps = {
  currentClient: undefined,
  setNewBasicData: null,
  setShowMessage: null,
  isSaveBasicData: false,
};

BasicDataForm.propTypes = {
  updating: PropTypes.bool.isRequired,
  currentClient: ClientProps,
  setNewBasicData: PropTypes.func,
  setShowMessage: PropTypes.func,
  setIsSaveBasicData: PropTypes.func.isRequired,
  isSaveBasicData: PropTypes.bool,
  i18Tags: PropTypes.arrayOf().isRequired,
};

export default BasicDataForm;
